/**
 * The surface jiggle — ADR-038 §14 (J4): **each grown soft mass gets its own
 * membrane modes as morph targets, and its bounce leaf rings them.**
 *
 * TWO HALVES (the leaf's split: the run grafts, the viewport plays):
 *
 *  1. {@link graftSurfaceJiggle} — at the run's seat, after the leaves take
 *     their weight: per region, weld its patch (seams must not tear), solve 4
 *     modes, drop the first (a pure volume change — flesh is incompressible)
 *     and make the 3 kept ones volume-neutral, scale each to a peak of 1, and
 *     graft them as `surface.<Region>.<k>` targets with normal deltas (the
 *     moving highlight). Grafted in the run, never lazily in a frame: the
 *     renderer must see the targets before the mesh first draws.
 *  2. {@link createSurfaceJiggle} — each frame, after the leaf springs have
 *     advanced: the leaf tip's velocity change RELATIVE to its owner, carried
 *     into the body's bind frame, kicks each mode through its participation
 *     vector (Σ Mφn / Σ Mφ² — the inertial projection); exact oscillators step;
 *     the saturated weights (metres of peak displacement) are written to the
 *     mesh and restored after the frame, so every door that reads the scene
 *     between frames sees the rest shape.
 *
 * THE NUMBERS (§14.2, §14.10; R14 Claude's call, tuned by Allen's eye):
 * the lowest kept mode rings at RATIO × the bounce's rate, the rest at
 * √(λk/λ1) above it; one ζ per region sets the lifespan to LIFE × the bounce's
 * ring; the render cap is CAP × the mass's prominence.
 *
 * ON IN THE APP (field-passed 2026-09-28; `?j4=off` is the logged kill switch). The run
 * option defaults OFF, so a headless caller or a test grafts only when it asks.
 * The export writes the modes SPARSE with their declaration beside the bounce's
 * (`extras.poqpoq.surface`, §14.5); a reader that knows none of it plays the rest shape.
 *
 * Depends on: three, kernel (modes, oscillator), adapter-three (the morph
 * door, subject arrays). Depended on by: the run (graft), main (the frame).
 */
import { Matrix3, Matrix4, Vector3, type Object3D, type SkinnedMesh } from 'three';
import { declaredSoftTissue, graftSurfaceMorph, type SubjectArrays, type SurfaceDelta } from '@harryhausen/adapter-three';
import {
  createModalOscillator,
  kindOfRegion,
  softTissueRegionOfSpring,
  softTissueSpringName,
  solvePatchModes,
  SurfaceModesRefusal,
  volumeNeutral,
  weldPatch,
  zetaForLifespan,
  type ModalOscillator,
  type SoftTissueControls,
  type JiggleKind,
  type JiggleRegionId,
} from '@harryhausen/kernel';

/** Kept modes per mass (R13). */
export const SURFACE_MODES = 3;
/** Hair keeps more (Allen 2026-10-01: more jiggle; a lock-length patch has room for finer ripples). */
export const HAIR_SURFACE_MODES = 5;
/** Hair's render cap as a multiple of the tuning's (its leaf is long, its prominence small). */
export const HAIR_CAP_SCALE = 3;

export interface SurfaceJiggleMode {
  /** The morph target's name and index on the subject mesh. */
  readonly target: string;
  readonly index: number;
  readonly lambda: number;
  /** Bind-world participation vector: Δv · this = the mode's velocity kick. */
  readonly participation: readonly [number, number, number];
}

export interface SurfaceJiggleRecord {
  readonly region: JiggleRegionId;
  readonly kind: JiggleKind;
  /** The leaf's tip and the bone it hangs from (the owner), as scene names. */
  readonly tip: string;
  readonly owner: string;
  readonly prominence: number;
  readonly modes: readonly SurfaceJiggleMode[];
  /** Welded patch size and the solve's time. */
  readonly vertices: number;
  readonly solveMs: number;
  /** Why this region has no surface jiggle (the bounce stands), or null. */
  readonly refusal: string | null;
}

/** One bounce leaf, wherever it came from (grown this session, or declared by the file). */
export interface SurfaceJiggleLeaf {
  readonly region: JiggleRegionId;
  /** The leaf bone, its weightless tip, and the bone it hangs from — scene names. */
  readonly leaf: string;
  readonly tip: string;
  readonly owner: string;
  /** Root → tip, bind-world metres (the cap's scale: ≈ 1.5 × the mass's prominence, THE ROOT). */
  readonly length: number;
  /**
   * The patch the modes solve on, when it is not the leaf's own flesh (ADR-039 T2a): the
   * confirmed hair region, and the vertices HELD (φ = 0) beyond its topological ring —
   * the scalp shell, the body it rests on, the face. Absent: the leaf's own flesh.
   */
  readonly patch?: { readonly vertices: readonly number[]; readonly faces: readonly number[]; readonly held: ReadonlySet<number> };
}

/**
 * Every bounce leaf on this subject: grown this run (the plan's records), or
 * declared by the file (`jiggle-<Region>` springs on a reopened export).
 */
export function surfaceJiggleLeaves(
  scene: Object3D,
  grown: readonly { readonly region: JiggleRegionId; readonly bone: string; readonly tip: string; readonly parent: string; readonly length: number }[],
): SurfaceJiggleLeaf[] {
  const leaves: SurfaceJiggleLeaf[] = grown.map((r) => ({ region: r.region, leaf: r.bone, tip: r.tip, owner: r.parent, length: r.length }));
  for (const d of declaredSoftTissue(scene)) {
    const region = d.extras?.region ?? softTissueRegionOfSpring(d.decl);
    if (region === null || leaves.some((l) => l.region === region)) continue;
    const [first, second] = d.decl.joints;
    const node = first === undefined ? undefined : scene.getObjectByName(first.bone);
    const tipNode = second === undefined ? undefined : scene.getObjectByName(second.bone);
    if (node?.parent == null || tipNode === undefined) continue;
    leaves.push({ region, leaf: node.name, tip: tipNode.name, owner: node.parent.name, length: node.getWorldPosition(new Vector3()).distanceTo(tipNode.getWorldPosition(new Vector3())) });
  }
  return leaves;
}

/**
 * Half 1: solve and graft every leaf's modes onto the sole skinned mesh. THE
 * PATCH IS THE LEAF'S OWN FLESH — every vertex the leaf carries weight on, every
 * face with all three corners in it — so a grown leaf and a file's leaf are the
 * same case, and the ring falls just inside the feather's edge. `arrays` is the
 * subject in bind-world (current paint). A patch the solver refuses records why
 * and keeps its bounce.
 */
export function graftSurfaceJiggle(mesh: SkinnedMesh, arrays: SubjectArrays, leaves: readonly SurfaceJiggleLeaf[]): SurfaceJiggleRecord[] {
  const normal = mesh.geometry.getAttribute('normal');
  const linear = new Matrix3().setFromMatrix4(mesh.bindMatrix);
  const toLocal = linear.clone().invert(); // world displacement → geometry space
  const toLocalNormal = linear.clone().transpose(); // world normal delta → geometry space (n_w = L⁻ᵀ n_l)
  const normalToWorld = new Matrix3().getNormalMatrix(mesh.bindMatrix);
  const out: SurfaceJiggleRecord[] = [];
  const v = new Vector3();
  const skin = arrays.skin;
  const nVerts = arrays.positions.length / 3;
  for (const leaf of leaves) {
    const base = { region: leaf.region, kind: kindOfRegion(leaf.region), tip: leaf.tip, owner: leaf.owner, prominence: leaf.length / 1.5 };
    const t0 = performance.now();
    try {
      if (normal === undefined) throw new SurfaceModesRefusal('the mesh has no normals to move along');
      if (skin === null) throw new SurfaceModesRefusal('the mesh is not skinned');
      const slot = arrays.bones.findIndex((b) => b.name === leaf.leaf);
      if (slot < 0) throw new SurfaceModesRefusal(`the leaf "${leaf.leaf}" carries no flesh on this mesh`);
      const inPatch = new Uint8Array(nVerts);
      let vertices: number[] = [];
      for (let i = 0; i < nVerts; i++) {
        for (let k = 0; k < 4; k++) {
          if (skin.index[i * 4 + k] === slot && skin.weight[i * 4 + k]! > 0) {
            inPatch[i] = 1;
            vertices.push(i);
            break;
          }
        }
      }
      let faces: number[] = [];
      for (let f = 0; f * 3 < arrays.indices.length; f++) {
        if (inPatch[arrays.indices[f * 3]!] && inPatch[arrays.indices[f * 3 + 1]!] && inPatch[arrays.indices[f * 3 + 2]!]) faces.push(f);
      }
      if (leaf.patch !== undefined) {
        vertices = [...leaf.patch.vertices];
        faces = [...leaf.patch.faces];
      }
      const patch = weldPatch(arrays.positions, vertices, faces, arrays.indices);
      // A declared patch's held vertices join its ring: a welded position is held when any twin is.
      if (leaf.patch !== undefined) patch.twins.forEach((twins, w) => {
        if (twins.some((t) => leaf.patch!.held.has(t))) patch.pinned[w] = 1;
      });
      const n = patch.twins.length;
      // Welded normals in bind-world: the twins' normals, averaged (a seam stays closed).
      const nw = new Float64Array(3 * n);
      patch.twins.forEach((twins, w) => {
        let x = 0, y = 0, z = 0;
        for (const t of twins) {
          v.fromBufferAttribute(normal, t).applyMatrix3(normalToWorld).normalize();
          x += v.x;
          y += v.y;
          z += v.z;
        }
        const l = Math.hypot(x, y, z) || 1;
        nw[w * 3] = x / l;
        nw[w * 3 + 1] = y / l;
        nw[w * 3 + 2] = z / l;
      });
      const keep = base.kind === 'hair' ? HAIR_SURFACE_MODES : SURFACE_MODES;
      const solved = solvePatchModes({ positions: patch.positions, indices: patch.indices, pinned: patch.pinned, count: keep + 1 });
      if (solved.modes.length < keep + 1) throw new SurfaceModesRefusal(`the patch has room for ${solved.modes.length} modes, not ${keep + 1}`);
      const [volume, ...rest] = solved.modes;
      const modes: SurfaceJiggleMode[] = [];
      rest.forEach((raw, k) => {
        const m = volumeNeutral(raw, volume!, solved.mass);
        let peak = 0;
        for (const x of m.phi) peak = Math.max(peak, Math.abs(x));
        if (!(peak > 0)) throw new SurfaceModesRefusal(`mode ${k + 1} is flat`);
        const name = `surface.${leaf.region}.${k + 1}`;
        let s = 1 / peak;
        // A mode's sign is the solver's choice. A file that already carries this target (a reopened
        // export) keeps its own deltas, so the mode takes THEIR sign — else the participation would
        // drive the stored shape the wrong way round (Codex P2, #233: surface.Hair.3 came back flipped).
        const slot = mesh.morphTargetDictionary?.[name];
        const stored = slot === undefined ? undefined : mesh.geometry.morphAttributes['position']?.[slot];
        if (stored !== undefined) {
          let dot = 0;
          const e = new Vector3();
          for (let w = 0; w < n; w++) {
            const phi = m.phi[w]!;
            if (phi === 0) continue;
            e.set(phi * nw[w * 3]!, phi * nw[w * 3 + 1]!, phi * nw[w * 3 + 2]!).applyMatrix3(toLocal);
            const t = patch.twins[w]![0]!;
            dot += e.x * stored.getX(t) + e.y * stored.getY(t) + e.z * stored.getZ(t);
          }
          if (dot < 0) s = -s;
        }
        // Participation: Σ M φ n / Σ M φ² (φ scaled to peak 1).
        let px = 0, py = 0, pz = 0, mm = 0;
        for (let w = 0; w < n; w++) {
          const f = m.phi[w]! * s;
          const mw = solved.mass[w]!;
          px += mw * f * nw[w * 3]!;
          py += mw * f * nw[w * 3 + 1]!;
          pz += mw * f * nw[w * 3 + 2]!;
          mm += mw * f * f;
        }
        const deltas: SurfaceDelta[] = [];
        const d = new Vector3();
        const g = new Vector3();
        for (let w = 0; w < n; w++) {
          const f = m.phi[w]! * s;
          if (f === 0) continue;
          d.set(f * nw[w * 3]!, f * nw[w * 3 + 1]!, f * nw[w * 3 + 2]!).applyMatrix3(toLocal);
          g.set(-m.gradient[w * 3]! * s, -m.gradient[w * 3 + 1]! * s, -m.gradient[w * 3 + 2]! * s).applyMatrix3(toLocalNormal);
          for (const t of patch.twins[w]!) deltas.push({ vertex: t, dx: d.x, dy: d.y, dz: d.z, nx: g.x, ny: g.y, nz: g.z });
        }
        const report = graftSurfaceMorph(mesh, name, deltas);
        modes.push({ target: name, index: report.index, lambda: m.lambda, participation: [px / mm, py / mm, pz / mm] });
      });
      out.push({ ...base, modes, vertices: n, solveMs: performance.now() - t0, refusal: null });
    } catch (err) {
      if (!(err instanceof SurfaceModesRefusal)) throw err;
      out.push({ ...base, modes: [], vertices: 0, solveMs: performance.now() - t0, refusal: err.message });
    }
  }
  return out;
}

// ─────────────────────────── half 2: the frame ───────────────────────────

/** The field-read dials (R14): all four are Allen's eye's to move. */
export interface SurfaceJiggleTuning {
  /** Surface rate ÷ bounce rate (the lowest kept mode). */
  readonly ratio: number;
  /** Surface lifespan ÷ the bounce's ring time. */
  readonly life: number;
  /** Render cap as a fraction of the mass's prominence. */
  readonly cap: number;
  /** A multiplier on each mass's Jiggle control (the `gain` URL dial; 1 = the control as set). */
  readonly gain: number;
}

export const SURFACE_JIGGLE_TUNING: SurfaceJiggleTuning = { ratio: 2, life: 1.5, cap: 0.08, gain: 1 }; // the Jiggle control's default 2 is Allen's eye, 2026-09-28 (Rita)

/**
 * The bounce's measured rate and ring (soft-tissue-spring.ts's ring sweep,
 * 2026-09-27): firmness sets the rate, drag the ring. Stiffness is per mass
 * and per kind step (breast 1, glute 1.5, belly 1.9), as the presets set it.
 */
const KIND_STEP: Readonly<Record<JiggleKind, number>> = { breast: 1, glute: 1.5, belly: 1.9, hair: 1 }; // hair (ADR-039 T2a): its 2.5 stiffness reads past Firm — the table's top, ≈ 5.6 Hz
const lerpTable = (table: readonly (readonly [number, number])[], x: number): number => {
  if (x <= table[0]![0]) return table[0]![1];
  for (let i = 1; i < table.length; i++) {
    const [x1, y1] = table[i]!;
    const [x0, y0] = table[i - 1]!;
    if (x <= x1) return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
  }
  return table[table.length - 1]![1];
};
export const bounceRateHz = (c: SoftTissueControls, kind: JiggleKind): number =>
  lerpTable([[0.4, 3.1], [0.8, 4.3], [1.5, 5.6]], c.stiffness / (KIND_STEP[kind] * Math.max(c.mass, 1e-3)));
export const bounceRingSeconds = (c: SoftTissueControls): number => lerpTable([[0.05, 0.75], [0.08, 0.4], [0.12, 0.2], [0.3, 0.2]], c.drag);

/** The numbers a mass's modes play at, from its bounce's controls and the dials (§14.2.4, §14.10). */
export function resolveSurface(rec: SurfaceJiggleRecord, c: SoftTissueControls, tuning: SurfaceJiggleTuning): { freqHz: number[]; zeta: number; cap: number; gain: number } {
  const f1 = tuning.ratio * bounceRateHz(c, rec.kind);
  const lambda1 = rec.modes[0]?.lambda ?? 1;
  return {
    freqHz: rec.modes.map((m) => f1 * Math.sqrt(Math.max(m.lambda, 1e-12) / Math.max(lambda1, 1e-12))),
    zeta: Math.min(0.9, zetaForLifespan(f1, tuning.life * bounceRingSeconds(c))),
    cap: tuning.cap * rec.prominence * (rec.kind === 'hair' ? HAIR_CAP_SCALE : 1),
    gain: tuning.gain * c.jiggle,
  };
}

/**
 * What the file declares for a mass's surface jiggle (§14.5), keyed by the bounce spring it
 * rides (`jiggle-<Region>`), under `extras.poqpoq.surface` — BESIDE the bounce's own
 * `jiggle` block, never inside it (that block is schema-parsed on read and would drop it).
 * A reader that knows none of this leaves every weight at 0: the rest shape, exactly.
 */
export interface SurfaceDeclaration {
  readonly version: 1;
  /** The leaf's tip and the bone whose BIND frame the participation vectors are in. */
  readonly tip: string;
  readonly owner: string;
  /** Kick: Δv of the tip, in the owner's bind frame, per fixed step · participation · gain → Δq̇ (metres/second). */
  readonly gain: number;
  readonly modes: readonly { readonly target: string; readonly freqHz: number; readonly zeta: number; readonly max: number; readonly participation: readonly [number, number, number] }[];
}

export interface SurfaceJiggleRun {
  readonly scene: Object3D;
  readonly surfaceJiggle: readonly SurfaceJiggleRecord[];
}

export interface SurfaceJiggleProbe {
  readonly region: JiggleRegionId;
  readonly refusal: string | null;
  readonly vertices: number;
  readonly solveMs: number;
  readonly freqHz: readonly number[];
  readonly zeta: number;
  readonly cap: number;
  readonly peak: number;
  readonly steps: number;
}

export interface SurfaceJiggle {
  /** Advance and write the weights; returns the restore (weights back to 0), or null when nothing plays. */
  applyFrame(run: SurfaceJiggleRun, frameDt: number, controlsOf: (region: JiggleRegionId) => SoftTissueControls | null): (() => void) | null;
  reset(run: SurfaceJiggleRun): void;
  tune(tuning: Partial<SurfaceJiggleTuning>): SurfaceJiggleTuning;
  probe(run: SurfaceJiggleRun): readonly SurfaceJiggleProbe[];
  /** Per spring name (`jiggle-<Region>`): `{ surface }` for the export's spring extras — masses whose bounce is on. */
  extrasForExport(run: SurfaceJiggleRun, controlsOf: (region: JiggleRegionId) => SoftTissueControls | null): Record<string, { surface: SurfaceDeclaration }>;
}

interface Live {
  rec: SurfaceJiggleRecord;
  osc: ModalOscillator | null;
  key: string;
  freqHz: number[];
  zeta: number;
  cap: number;
  prevT: Vector3 | null;
  prevV: Vector3 | null;
  peak: number;
  steps: number;
}

export function createSurfaceJiggle(): SurfaceJiggle {
  let tuning = { ...SURFACE_JIGGLE_TUNING };
  const lives = new WeakMap<Object3D, Map<JiggleRegionId, Live>>();
  const livesOf = (run: SurfaceJiggleRun): Map<JiggleRegionId, Live> => {
    let m = lives.get(run.scene);
    if (m === undefined) {
      m = new Map(run.surfaceJiggle.map((rec) => [rec.region, { rec, osc: null, key: '', freqHz: [], zeta: 0, cap: 0, prevT: null, prevV: null, peak: 0, steps: 0 }]));
      lives.set(run.scene, m);
    }
    return m;
  };
  const meshOf = (scene: Object3D): SkinnedMesh | null => {
    let found: SkinnedMesh | null = null;
    scene.traverse((o) => {
      if (found === null && (o as SkinnedMesh).isSkinnedMesh && (o as SkinnedMesh).morphTargetDictionary !== undefined) {
        const dict = (o as SkinnedMesh).morphTargetDictionary!;
        if (Object.keys(dict).some((k) => k.startsWith('surface.'))) found = o as SkinnedMesh;
      }
    });
    return found;
  };
  const bindWorldOf = (mesh: SkinnedMesh, name: string): Matrix4 | null => {
    const i = mesh.skeleton.bones.findIndex((b) => b.name === name);
    return i < 0 ? null : new Matrix4().copy(mesh.skeleton.boneInverses[i]!).invert();
  };
  const ownerBind = new WeakMap<SkinnedMesh, Map<string, Matrix4 | null>>();

  return {
    applyFrame(run, frameDt, controlsOf) {
      const mesh = meshOf(run.scene);
      if (mesh === null || mesh.morphTargetInfluences === undefined) return null;
      const influences = mesh.morphTargetInfluences;
      let binds = ownerBind.get(mesh);
      if (binds === undefined) ownerBind.set(mesh, (binds = new Map()));
      const touched: number[] = [];
      const dt = Math.min(Math.max(frameDt, 0), 0.25);
      for (const live of livesOf(run).values()) {
        const { rec } = live;
        const c = controlsOf(rec.region);
        if (rec.modes.length === 0 || c === null || !c.on) {
          live.prevT = null;
          live.prevV = null;
          continue;
        }
        // (Re)build the oscillator when the bounce's controls or the dials move.
        const { freqHz, zeta, cap } = resolveSurface(rec, c, tuning);
        const key = `${freqHz.map((x) => x.toFixed(4)).join(',')}|${zeta.toFixed(5)}|${cap.toFixed(6)}`;
        if (live.osc === null || live.key !== key) {
          live.osc = createModalOscillator(freqHz.map((f) => ({ freqHz: f, zeta, max: cap })));
          live.key = key;
          live.freqHz = freqHz;
          live.zeta = zeta;
          live.cap = cap;
        }
        // The leaf tip in its owner's BIND frame: at rest it is the tip's bind position,
        // and its motion is the leaf's swing expressed in the axes the modes were solved in.
        const tip = run.scene.getObjectByName(rec.tip);
        const owner = run.scene.getObjectByName(rec.owner);
        if (!binds.has(rec.owner)) binds.set(rec.owner, bindWorldOf(mesh, rec.owner));
        const ob = binds.get(rec.owner) ?? null;
        if (tip !== undefined && owner !== undefined && ob !== null && dt > 0) {
          owner.updateWorldMatrix(true, false);
          tip.updateWorldMatrix(true, false);
          const T = new Vector3().setFromMatrixPosition(tip.matrixWorld).applyMatrix4(new Matrix4().copy(owner.matrixWorld).invert()).applyMatrix4(ob);
          if (live.prevT !== null) {
            const vel = T.clone().sub(live.prevT).divideScalar(dt);
            if (live.prevV !== null && dt < 0.1) {
              const dv = vel.clone().sub(live.prevV);
              rec.modes.forEach((m, k) => live.osc!.kick(k, tuning.gain * c.jiggle * (dv.x * m.participation[0] + dv.y * m.participation[1] + dv.z * m.participation[2])));
            }
            live.prevV = vel;
          }
          live.prevT = T;
        }
        live.osc.step(dt);
        live.steps++;
        const w = live.osc.weights();
        rec.modes.forEach((m, k) => {
          influences[m.index] = w[k]!;
          touched.push(m.index);
          live.peak = Math.max(live.peak, Math.abs(w[k]!));
        });
      }
      if (touched.length === 0) return null;
      return () => {
        for (const i of touched) influences[i] = 0;
      };
    },
    reset(run) {
      for (const live of livesOf(run).values()) {
        live.osc?.reset();
        live.prevT = null;
        live.prevV = null;
        live.peak = 0;
      }
    },
    tune(next) {
      tuning = { ...tuning, ...next };
      return tuning;
    },
    extrasForExport(run, controlsOf) {
      const out: Record<string, { surface: SurfaceDeclaration }> = {};
      for (const rec of run.surfaceJiggle) {
        const c = controlsOf(rec.region);
        if (c === null || !c.on || rec.modes.length === 0) continue;
        const r = resolveSurface(rec, c, tuning);
        out[softTissueSpringName(rec.region)] = {
          surface: {
            version: 1,
            tip: rec.tip.replace(/_graft$/, ''),
            owner: rec.owner.replace(/_graft$/, ''),
            gain: r.gain,
            modes: rec.modes.map((m, k) => ({ target: m.target, freqHz: r.freqHz[k]!, zeta: r.zeta, max: r.cap, participation: m.participation })),
          },
        };
      }
      return out;
    },
    probe(run) {
      return [...livesOf(run).values()].map((l) => ({
        region: l.rec.region,
        refusal: l.rec.refusal,
        vertices: l.rec.vertices,
        solveMs: Math.round(l.rec.solveMs),
        freqHz: l.freqHz.map((f) => +f.toFixed(2)),
        zeta: +l.zeta.toFixed(4),
        cap: l.cap,
        peak: l.peak,
        steps: l.steps,
      }));
    },
  };
}
